import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  OnboardingRequestService,
  OnboardingRequest,
  OnboardingRequestStatus,
} from '../../../core/services/onboarding-request.service';
import { ComponentsModule } from '../../../components/components.module';
import { PermissionService } from '../../../core/services/permission.service';
import { BadgeColor } from '../../../components/ui/badge/badge.component';
import { ConfirmationService } from '../../../core/services/confirmation.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-onboarding-request-detail',
  templateUrl: './onboarding-request-detail.component.html',
  styleUrls: ['./onboarding-request-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ComponentsModule, RouterModule],
})
export class OnboardingRequestDetailComponent implements OnInit {
  request: OnboardingRequest | null = null;
  isLoading = true;
  internalNotes = '';
  isSavingNotes = false;
  canEdit = false;

  // Reject modal
  isRejectModalOpen = false;
  rejectionReason = '';
  isRejecting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private onboardingRequestService: OnboardingRequestService,
    private cdr: ChangeDetectorRef,
    private permissionService: PermissionService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.canEdit = this.permissionService.canEditForRole();
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.loadRequest(id);
    }
    this.showHotelCreatedSuccessIfRedirected();
  }

  private showHotelCreatedSuccessIfRedirected(): void {
    const state = history.state as { hotelCreatedSuccess?: boolean; successMessage?: string };
    if (state?.hotelCreatedSuccess && state?.successMessage) {
      this.confirmationService.swalSuccess(state.successMessage);
    }
  }

  private loadRequest(id: number): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.onboardingRequestService.getRequest(id).subscribe({
      next: (response) => {
        this.request = response.request;
        this.internalNotes = this.request.internal_notes || '';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.goBack();
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/onboarding-requests']);
  }

  viewHotel(): void {
    if (this.request?.created_hotel_id) {
      this.router.navigate(['/' + this.request.created_hotel_id + '/dashboard']);
    }
  }

  saveNotes(): void {
    if (!this.request) return;

    this.isSavingNotes = true;
    this.cdr.markForCheck();

    this.onboardingRequestService.updateNotes(this.request.id, this.internalNotes).subscribe({
      next: (response) => {
        this.request = response.request;
        this.isSavingNotes = false;
        this.confirmationService.swalSuccess(this.translate.instant('ONBOARDING_REQUESTS.NOTES_SAVED'));
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSavingNotes = false;
        this.confirmationService.swalError(this.translate.instant('notifications.error.save_failed'));
        this.cdr.markForCheck();
      },
    });
  }

  markAsContacted(): void {
    if (!this.request) return;

    this.onboardingRequestService
      .updateStatus(this.request.id, 'contacted', this.internalNotes)
      .subscribe({
        next: (response) => {
          this.request = response.request;
          this.cdr.markForCheck();
        },
      });
  }

  markAsInProgress(): void {
    if (!this.request) return;

    this.onboardingRequestService
      .updateStatus(this.request.id, 'in_progress', this.internalNotes)
      .subscribe({
        next: (response) => {
          this.request = response.request;
          this.cdr.markForCheck();
        },
      });
  }

  openRejectModal(): void {
    this.rejectionReason = '';
    this.isRejectModalOpen = true;
    this.cdr.markForCheck();
  }

  closeRejectModal(): void {
    this.isRejectModalOpen = false;
    this.rejectionReason = '';
    this.cdr.markForCheck();
  }

  confirmReject(): void {
    if (!this.request || !this.rejectionReason.trim()) return;

    this.isRejecting = true;
    this.cdr.markForCheck();

    this.onboardingRequestService.rejectRequest(this.request.id, this.rejectionReason).subscribe({
      next: (response) => {
        this.request = response.request;
        this.isRejecting = false;
        this.isRejectModalOpen = false;
        this.confirmationService.swalSuccess(this.translate.instant('ONBOARDING_REQUESTS.REJECTION_SUCCESS'));
        this.cdr.markForCheck();
      },
      error: () => {
        this.isRejecting = false;
        this.confirmationService.swalError(this.translate.instant('notifications.error.save_failed'));
        this.cdr.markForCheck();
      },
    });
  }

  getStatusVariant(status: OnboardingRequestStatus): BadgeColor {
    const statusMap: Record<OnboardingRequestStatus, BadgeColor> = {
      pending: 'warning',
      contacted: 'secondary',
      in_progress: 'secondary',
      completed: 'success',
      rejected: 'primary',
    };
    return statusMap[status] || 'neutral';
  }

  canProcess(): boolean {
    if (!this.request) return false;
    return ['pending', 'contacted', 'in_progress'].includes(this.request.status);
  }

  createHotelFromRequest(): void {
    if (!this.request) return;
    this.router.navigate(['/hotel-creation'], {
      queryParams: { onboardingRequestId: this.request.id },
    });
  }

  formatContactPosition(slug: string | null | undefined): string {
    if (!slug) return '';
    const key = `ONBOARDING_REQUEST.POSITION_OPTIONS.${slug.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : slug;
  }

  formatReferralSource(slug: string | null | undefined): string {
    if (!slug) return '';
    const key = `ONBOARDING_REQUEST.REFERRAL_OPTIONS.${slug.toUpperCase()}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : slug;
  }
}
